import { useCallback, useEffect } from 'react';
import * as React from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { ReactComponent as ArrowLeft } from './arrow-left.svg';
import { ReactComponent as ArrowRight } from './arrow-right.svg';

export interface CarouselProps {
  activeIndex?: number;
  children?: React.ReactNode;
  className?: string;
  onActiveIndexChange?: (newIndex: number) => void;
  shouldAllowKeyboardNavigation?: boolean;
}

export const Carousel = ({
  activeIndex = 0,
  children,
  className,
  onActiveIndexChange,
  shouldAllowKeyboardNavigation = true,
}: CarouselProps) => {
  const slides = React.Children.toArray(children);

  const lastIndex = slides.length - 1;

  const goBack = useCallback(() => {
    onActiveIndexChange?.(activeIndex === 0 ? activeIndex : activeIndex - 1);
  }, [activeIndex, onActiveIndexChange]);

  const goForward = useCallback(() => {
    onActiveIndexChange?.(activeIndex === lastIndex ? activeIndex : activeIndex + 1);
  }, [activeIndex, lastIndex, onActiveIndexChange]);

  useEffect(() => {
    if (!shouldAllowKeyboardNavigation) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goBack();
      } else if (event.key === 'ArrowRight') {
        goForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goBack, goForward, shouldAllowKeyboardNavigation]);

  return (
    <Container className={className}>
      <div tw="flex flex-col flex-grow py-4 xl:py-6">
        <div tw="flex-grow relative">
          <div tw="absolute inset-0 overflow-hidden">{slides[activeIndex]}</div>
        </div>
      </div>
      <ArrowWrapper
        tw="order-first"
        aria-disabled={activeIndex === 0}
        aria-label="Previous"
        isDisabled={activeIndex === 0}
        onClick={goBack}
        role="button"
      >
        <ArrowLeft tw="fill-current text-white" />
      </ArrowWrapper>
      <ArrowWrapper
        aria-disabled={activeIndex === lastIndex}
        aria-label="Next"
        isDisabled={activeIndex === lastIndex}
        onClick={goForward}
        role="button"
      >
        <ArrowRight tw="fill-current text-white" />
      </ArrowWrapper>
    </Container>
  );
};

const Container = tw.div`flex relative`;

const ArrowWrapper = styled.div(({ isDisabled }: { isDisabled?: boolean }) => [
  tw`cursor-pointer flex items-center px-4 select-none`,
  isDisabled && tw`invisible pointer-events-none`,
]);
